import { Checkbox } from "@website/ui/checkbox";
import { MiniChips } from "@website/ui/mini-chips";
import { FilterCollapse } from "@website/widget/filter/collapse";
import { CheckboxTypes } from "@website/widget/filter/filter.types";
import styles from "@website/widget/filter/index.module.scss";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

interface CheckboxFilterProperties extends CheckboxTypes {
  name: string;
  collapseClassName?: string;
  containerClassName?: string;
}

export const CheckboxFilter = (properties: CheckboxFilterProperties) => {
  const {
    name,
    title,
    collapsible,
    defaultValue,
    type,
    options = [],
    isNew = false,
    showTitle = true,
    collapseClassName,
    containerClassName,
    onChange
  } = properties;
  const router = useRouter();
  const { control, setValue } = useFormContext();

  const parsedInitialValues = useMemo(() => {
    const query = router.query[name];

    const stringifyQuery = Array.isArray(query) ? query.join(",") : query;

    const data = stringifyQuery || defaultValue;

    return Object.fromEntries(data?.split(",").map((q) => [q, true]) ?? []);
  }, [defaultValue, name, router.query]);

  useEffect(() => {
    setValue(name, { type, value: parsedInitialValues ?? {} });
  }, [name, setValue, parsedInitialValues, type]);

  const checkedByQuery = useCallback(
    (optionName: string) => {
      if (!router.query[name]) return false;

      return toString(router.query[name]).split(",").includes(optionName);
    },
    [name, router.query]
  );

  return (
    <Fragment>
      <div className="inline-grid grid-cols-2 items-center gap-2">
        {showTitle && <span className={styles["filter-header"]}>{title}</span>}
        {isNew && (
          <MiniChips color="primary" text="جدید" variant="round-contained" />
        )}
      </div>
      <FilterCollapse
        active={collapsible}
        className={collapseClassName}
        containerClassName={containerClassName}
      >
        {options.map((option) => (
          <div
            className={styles["filter-checkbox-container"]}
            key={name + option.name}
          >
            <Controller
              control={control}
              name={`${name}.value.${option.name}`}
              render={({
                field: { onChange: fieldOnChange, value, ...field }
              }) => (
                <Checkbox
                  checked={checkedByQuery(option.name) ?? value}
                  className={styles["filter-checkbox"]}
                  color="secondary"
                  label={option.label}
                  onChange={(event) => {
                    fieldOnChange(event);
                    if (onChange) onChange(option);
                  }}
                  {...field}
                />
              )}
            />
            {option.icon}
          </div>
        ))}
      </FilterCollapse>
    </Fragment>
  );
};
